import { useState, type FormEvent } from 'react';

type Status = 'idle' | 'submitting' | 'ok' | 'error';

type Props = { source?: string };

/** Newsletter signup. POSTs to /api/subscribe, which writes the row and
 * fires a confirmation email via Resend. The Worker treats duplicates the
 * same as fresh signups (re-sends the confirm link), so this form never
 * needs to distinguish them. */
export function SignupForm({ source = 'homepage' }: Props) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg(null);

    try {
      const res = await fetch('/api/subscribe', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), source }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        if (j.error === 'invalid_email') {
          setErrorMsg("That doesn't look like a valid email.");
        } else if (j.error === 'newsletter_not_configured') {
          setErrorMsg('Signup is offline. Try again later.');
        } else {
          setErrorMsg('Signup failed. Try again.');
        }
        setStatus('error');
        return;
      }
      setStatus('ok');
      setEmail('');
    } catch {
      setErrorMsg('Network error. Try again.');
      setStatus('error');
    }
  }

  if (status === 'ok') {
    return (
      <div
        style={{
          padding: '12px 14px',
          border: '1px solid var(--ink)',
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: 1,
        }}
      >
        ✓ Check your inbox to confirm.
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'stretch',
      }}
    >
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        autoComplete="email"
        aria-label="Your email address"
        style={{
          flex: 1,
          fontFamily: 'var(--font-mono)',
          fontSize: 13,
          padding: '10px 12px',
          background: 'var(--paper)',
          color: 'var(--ink)',
          border: '1px solid var(--ink)',
          borderRadius: 0,
        }}
      />
      <button
        type="submit"
        disabled={status === 'submitting'}
        style={{
          padding: '10px 16px',
          background: 'var(--ink)',
          color: 'var(--paper)',
          border: '1px solid var(--ink)',
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: 1.4,
          cursor: status === 'submitting' ? 'wait' : 'pointer',
        }}
      >
        {status === 'submitting' ? 'Sending…' : '▸ Subscribe'}
      </button>
      {status === 'error' && errorMsg && (
        <div
          role="alert"
          style={{
            position: 'absolute',
            marginTop: 44,
            fontSize: 11,
            color: 'var(--accent)',
            textTransform: 'uppercase',
            letterSpacing: 0.8,
          }}
        >
          {errorMsg}
        </div>
      )}
    </form>
  );
}
